import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  Grid,
  CircularProgress,
} from '@mui/material';
import { AccountType, Currency, UpdateAccountRequest, AccountWithBalance } from '../types/api';
import { accountsService } from '../services/accounts-service';

interface EditAccountDialogProps {
  open: boolean;
  account: AccountWithBalance | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormData {
  name: string;
  description: string;
  account_type: AccountType;
  currency: Currency;
  institution: string;
  account_number: string;
}

interface FormErrors {
  name?: string;
  account_type?: string;
  currency?: string;
  general?: string;
}

const ACCOUNT_TYPES: { value: AccountType; label: string; description: string }[] = [
  { value: 'Asset', label: 'Asset', description: 'Checking, savings, cash accounts' },
  { value: 'Investment', label: 'Investment', description: 'Stocks, bonds, mutual funds, ETFs' },
  { value: 'Debt', label: 'Debt', description: 'Credit cards, loans, mortgages' },
];

// Every currency the server's validCurrencies map accepts. A code missing
// here cannot be picked, which is indistinguishable from the server refusing
// it -- and is why INR accounts could not be created.
const CURRENCIES: { value: Currency; label: string }[] = [
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'INR', label: 'INR - Indian Rupee' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'JPY', label: 'JPY - Japanese Yen' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
  { value: 'CHF', label: 'CHF - Swiss Franc' },
  { value: 'CNY', label: 'CNY - Chinese Yuan' },
];

export const EditAccountDialog: React.FC<EditAccountDialogProps> = ({
  open,
  account,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    account_type: 'Asset',
    currency: 'CAD',
    institution: '',
    account_number: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form data when account prop changes
  useEffect(() => {
    if (account) {
      setFormData({
        name: account.name,
        description: account.description || '',
        account_type: account.account_type,
        currency: account.currency,
        institution: account.institution || '',
        // Deliberately blank. The API returns only the last 4, because the
        // full number is stored encrypted and no endpoint decrypts it, so
        // there is nothing to prefill. Leaving the box empty and sending
        // nothing means an unrelated edit keeps the stored number; typing a
        // value replaces it.
        account_number: '',
      });
    }
  }, [account]);

  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Account name is required';
    }
    
    if (!formData.account_type) {
      newErrors.account_type = 'Account type is required';
    }
    
    if (!formData.currency) {
      newErrors.currency = 'Currency is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!account || !validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const updateData: UpdateAccountRequest = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        institution: formData.institution.trim() || undefined,
        account_number: formData.account_number.trim() || undefined,
        metadata: account.metadata, // Preserve existing metadata
      };

      await accountsService.updateAccount(account.id, updateData);
      
      onSuccess?.();
      handleClose();
    } catch (error: any) {
      console.error('Failed to update account:', error);
      setErrors({
        general: error.message || 'Failed to update account. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    
    setErrors({});
    onClose();
  };

  if (!account) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="edit-account-dialog-title"
    >
      <DialogTitle id="edit-account-dialog-title">
        Edit Account: {account.name}
      </DialogTitle>
      
      <DialogContent>
        {errors.general && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general}
          </Alert>
        )}

        <Box component="form" sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {/* Account Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Name"
                value={formData.name}
                onChange={handleInputChange('name')}
                error={!!errors.name}
                helperText={errors.name || 'e.g., "TD Checking Account"'}
                required
                disabled={isSubmitting}
              />
            </Grid>

            {/* Account Type (read-only) */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Type"
                value={formData.account_type}
                InputProps={{
                  readOnly: true,
                }}
                helperText="Account type cannot be changed after creation"
                disabled
              />
            </Grid>

            {/* Currency (read-only) */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Currency"
                value={`${formData.currency} - ${CURRENCIES.find(c => c.value === formData.currency)?.label.split(' - ')[1]}`}
                InputProps={{
                  readOnly: true,
                }}
                helperText="Currency cannot be changed after creation"
                disabled
              />
            </Grid>

            {/* Current Balance (read-only) */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Current Balance"
                value={`${account.currency} ${parseFloat(account.current_amount || '0').toFixed(2)}`}
                InputProps={{
                  readOnly: true,
                }}
                helperText="Balance is managed through snapshots"
                disabled
              />
            </Grid>

            {/* Institution */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Institution"
                value={formData.institution}
                onChange={handleInputChange('institution')}
                helperText="Bank or financial institution name"
                disabled={isSubmitting}
              />
            </Grid>

            {/* Account Number */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Number"
                value={formData.account_number}
                onChange={handleInputChange('account_number')}
                placeholder={
                  account?.account_number_last4
                    ? `Currently ending ${account.account_number_last4}`
                    : undefined
                }
                helperText={
                  account?.account_number_last4
                    ? 'Leave blank to keep the stored number. Encrypted at rest; only the last 4 are ever shown.'
                    : 'Optional. Encrypted at rest; only the last 4 are ever shown.'
                }
                disabled={isSubmitting}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={formData.description}
                onChange={handleInputChange('description')}
                helperText="Optional notes about this account"
                disabled={isSubmitting}
              />
            </Grid>

            {/* Account Info */}
            <Grid item xs={12}>
              <Alert severity="info">
                <Box>
                  <strong>Account created:</strong> {new Date(account.created_at).toLocaleDateString()}
                </Box>
                <Box>
                  <strong>Last updated:</strong> {new Date(account.updated_at).toLocaleDateString()}
                </Box>
                {account.last_snapshot_date && (
                  <Box>
                    <strong>Last snapshot:</strong> {new Date(account.last_snapshot_date).toLocaleDateString()}
                  </Box>
                )}
              </Alert>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={handleClose} 
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
        >
          {isSubmitting ? 'Updating...' : 'Update Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};